import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Target, Rocket, Heart, ChevronDown } from 'lucide-react';
import { useLang } from '../hooks/useLang';
import '../styles/pages/About.scss';
import Navbar from '../components/common/Navbar';
import teamImg from '../assets/images/about_us.png';

const About = () => {
    const { language } = useLang();
    const visionRef = React.useRef(null);

    const scrollToVision = () => {
        visionRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.3,
                delayChildren: 0.2
            }
        }
    };

    const itemVariants = {
        hidden: { y: 30, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: { duration: 0.8, ease: "easeOut" }
        }
    };

    const floatingVariant = {
        animate: {
            y: [0, -20, 0],
            rotate: [0, 2, -2, 0],
            transition: {
                duration: 6,
                repeat: Infinity,
                ease: "easeInOut"
            }
        }
    };

    return (
        <div className="about-page">
            <Navbar />

            <div className="about-content-wrapper">
                <motion.section
                    className="about-hero"
                    initial="hidden"
                    animate="visible"
                    variants={containerVariants}
                >
                    <div className="hero-content">
                        <motion.h1 variants={itemVariants} className="hero-title">
                            We believe that <span className="highlight">good design</span> is powerful,
                            <span className="highlight"> hard work</span> is essential, and
                            exploring the <span className="highlight">unknown</span> is important.
                        </motion.h1>
                        <motion.p variants={itemVariants} className="hero-subtitle">
                            Empowering students and creators to share knowledge, collaborate, and reach new heights in their academic journey.
                        </motion.p>
                        <motion.div
                            variants={itemVariants}
                            className="scroll-indicator"
                            onClick={scrollToVision}
                            whileHover={{ scale: 1.2, cursor: 'pointer' }}
                        >
                            <motion.div animate={{ y: [0, 10, 0] }} transition={{ repeat: Infinity, duration: 1.5 }}>
                                <ChevronDown size={32} />
                            </motion.div>
                        </motion.div>
                    </div>
                </motion.section>

                <section className="about-vision" ref={visionRef}>
                    <div className="container">
                        <motion.div
                            className="vision-grid"
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, margin: "-100px" }}
                            variants={containerVariants}
                        >
                            <motion.div className="vision-text" variants={itemVariants}>
                                <motion.span
                                    className="accent-line"
                                    initial={{ width: 0 }}
                                    whileInView={{ width: 60 }}
                                    transition={{ duration: 1 }}
                                ></motion.span>
                                <h2>Defining the Future of Note Sharing</h2>
                                <p>
                                    YeniDers started with a simple idea: making academic resources accessible to everyone.
                                    We've built more than just a platform; we've built a community that thrives on mutual support and collective growth.
                                </p>
                            </motion.div>
                            <motion.div className="vision-visual" variants={floatingVariant} animate="animate">
                                <div className="abstract-shape shape-1"></div>
                                <div className="abstract-shape shape-2"></div>
                                <div className="abstract-glass-card">
                                    <div className="glass-inner">
                                        <Rocket size={40} color="#fff" />
                                    </div>
                                </div>
                            </motion.div>
                        </motion.div>
                    </div>
                </section>

                <section className="about-services">
                    <div className="container">
                        <motion.h2
                            className="section-title"
                            initial={{ opacity: 0, letterSpacing: "10px" }}
                            whileInView={{ opacity: 1, letterSpacing: "0px" }}
                            viewport={{ once: true }}
                            transition={{ duration: 1 }}
                        >
                            How We Help You
                        </motion.h2>
                        <div className="services-grid">
                            {[
                                { icon: <Target />, title: "Precision Tools", desc: "AI-powered tools tailored for student needs, from PDF conversion to quiz generation." },
                                { icon: <Rocket />, title: "Rapid Growth", desc: "Accelerate your learning path with shared materials and collaborative study groups." },
                                { icon: <Users />, title: "Community First", desc: "A vibrant network of students from across the country helping each other succeed." },
                                { icon: <Heart />, title: "Built with Passion", desc: "Every feature is designed with a relentless pursuit of excellence and user discovery." }
                            ].map((service, index) => (
                                <motion.div
                                    key={index}
                                    className="service-card"
                                    initial={{ scale: 0.9, opacity: 0 }}
                                    whileInView={{ scale: 1, opacity: 1 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: index * 0.1 }}
                                    whileHover={{
                                        y: -20,
                                        boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                                        backgroundColor: "rgba(255,255,255,0.25)"
                                    }}
                                >
                                    <div className="service-icon">{service.icon}</div>
                                    <h3>{service.title}</h3>
                                    <p>{service.desc}</p>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="about-team">
                    <div className="container">
                        <motion.div
                            className="team-layout"
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                            variants={containerVariants}
                        >
                            <motion.div className="team-image-container" variants={itemVariants}>
                                <img src={teamImg} alt="Our Collective Team" className="team-image" />
                                <div className="image-overlay"></div>
                            </motion.div>
                            <motion.div className="team-content" variants={itemVariants}>
                                <span className="subtitle">The Collective</span>
                                <h2>Driven by Innovation, Bound by Purpose</h2>
                                <p>
                                    We are a diverse collective of designers, developers, and educators.
                                    We work with individuals who understand that design is a process and our raw ingredient is time.
                                    Collaboration is a must, egos are a no-go, and we prefer to do the work instead of just talk about it.
                                </p>
                            </motion.div>
                        </motion.div>
                    </div>
                </section>
            </div>

            <section className="about-socials" style={{ padding: '60px 0', textAlign: 'center' }}>
                <div className="container">
                    <motion.div
                        className="social-links-grid"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={containerVariants}
                    >
                        <motion.h2 variants={itemVariants} style={{ fontSize: '2rem', marginBottom: '30px', color: 'var(--text)' }}>Connect With Us</motion.h2>
                        <motion.div 
                            className="social-links-container" 
                            variants={itemVariants}
                            style={{ display: 'flex', gap: '20px', justifyContent: 'center', flexWrap: 'wrap' }}
                        >
                            {[
                                { name: 'LinkedIn', url: 'https://www.linkedin.com/company/yeniders/' },
                                { name: 'Instagram', url: 'https://www.instagram.com/yeniders.az/' },
                                { name: 'Facebook', url: 'https://www.facebook.com/p/Yenidersaz-61579500553181/' },
                                { name: 'X', url: 'https://x.com/yeniders' },
                                { name: 'Pinterest', url: 'https://www.pinterest.com/yeniderscom/' },
                                { name: 'Medium', url: 'https://yeniders.medium.com/' }
                            ].map((link) => (
                                <a 
                                    key={link.name}
                                    href={link.url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    style={{
                                        padding: '10px 24px',
                                        borderRadius: '30px',
                                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                        color: 'var(--text)',
                                        textDecoration: 'none',
                                        fontWeight: '600',
                                        border: '1px solid rgba(255, 255, 255, 0.2)',
                                        transition: 'all 0.3s ease'
                                    }}
                                    onMouseOver={(e) => { e.currentTarget.style.backgroundColor = 'var(--primary)'; e.currentTarget.style.color = 'var(--bg)'; e.currentTarget.style.borderColor = 'var(--primary)'; }}
                                    onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'; e.currentTarget.style.color = 'var(--text)'; e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)'; }}
                                >
                                    {link.name}
                                </a>
                            ))}
                        </motion.div>
                    </motion.div>
                </div>
            </section>

            <footer className="about-footer-belt">
                <motion.div
                    className="belt-content"
                    animate={{ x: ["0%", "-50%"] }}
                    transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
                >
                    <span>INNOVATION • COLLABORATION • COMMUNITY • GROWTH • LEARNING • DISCOVERY • </span>
                    <span>INNOVATION • COLLABORATION • COMMUNITY • GROWTH • LEARNING • DISCOVERY • </span>
                </motion.div>
            </footer>
        </div>
    );
};

export default About;
